import axios from 'axios';
import { auth } from '../../../firebase';
import { cleanJsonEmptyValues } from '../../../utils/formatUtils';
import {
  JobPhasesCostCode,
  JobPhasesFormFields,
} from '../../features/accounting/forms/JobPhases/JobPhasesForm';
import {
  APEntryDraftSummary,
  APTransactionEntryArgs,
  APTransactionEntryDraft,
  APTransactionEntryUpdateArgs,
  ContractDraft,
  FormHistoryItem,
  NewContractFields,
  POArgs,
  SLArgs,
  SubcontractChangeOrderArgs,
} from '../../Interfaces/Accounting/ACForms.interfaces';
import { Company } from '../../Interfaces/admin/User.interfaces';
import { buildContractDraftFormData } from '../../features/accounting/forms/NewContract/utils';

const apiUrl = process.env['NX_API_URL'];

export const fetchContractLookUpInfo = async (
  companyID: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(
    `${apiUrl}/api/accounting_forms/contractInfo`,
    {
      signal,
      params: { companyID },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const fetchJobLookUpInfo = async (
  companyID: string,
  department: number,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/accounting_forms/jobInfo`, {
    signal,
    params: { companyID, department },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const fetchNewContract = async (
  companyID: string,
  department?: number,
  signal?: AbortSignal
) => {
  const response = await axios.get(
    `${apiUrl}/api/accounting_forms/newContract`,
    {
      signal,
      params: { companyID, department },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const fetchContractGeneralInfo = async (
  companyID: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(
    `${apiUrl}/api/accounting_forms/contractGeneralInfo`,
    {
      signal,
      params: { companyID },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const fetchContractDraft = async (
  companyID: string,
  signal?: AbortSignal
): Promise<ContractDraft | null> => {
  const response = await axios.get(
    `${apiUrl}/api/accounting_forms/contract/draft`,
    {
      signal,
      params: { companyID },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const postContractDraft = async (
  companyID: string,
  body: NewContractFields
): Promise<{ draft: ContractDraft; error: string[] }> => {
  const formData = buildContractDraftFormData(companyID, body);

  const response = await axios.post(
    `${apiUrl}/api/accounting_forms/contract/draft`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const putContractDraft = async (
  id: string,
  companyID: string,
  body: NewContractFields,
  removeAttachments?: string[]
): Promise<{ draft: ContractDraft; error: string[] }> => {
  const formData = buildContractDraftFormData(
    companyID,
    body,
    removeAttachments
  );

  const response = await axios.put(
    `${apiUrl}/api/accounting_forms/contract/draft/${id}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const putSubmitContractDraft = async (id: string) => {
  const response = await axios.put(
    `${apiUrl}/api/accounting_forms/contract/draft/${id}/submit`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const fetchContractDraftAttachment = async (
  id: string,
  name: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(
    `${apiUrl}/api/accounting_forms/contract/draft/${id}/file`,
    {
      signal,
      params: { name },
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response;
};

export const postScheduleOfValues = async (
  companyID: string,
  schedule: File[]
) => {
  const formData = new FormData();
  schedule.forEach((file) => {
    formData.append('schedules', file);
  });
  formData.append('companyID', companyID);

  const response = await axios.post(
    `${apiUrl}/api/schedule_of_values/newSchedule`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const postEstimatedCosts = async (
  companyID: string,
  phases?: File,
  estimates?: File
) => {
  const formData = new FormData();

  if (phases) {
    formData.append('phases', phases);
  }

  if (estimates) {
    formData.append('estimates', estimates);
  }

  formData.append('companyID', companyID);

  const response = await axios.post(
    `${apiUrl}/api/estimated_cost/newEstimates`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const fetchPhaseMasterFile = async (companyID: string) => {
  const response = await axios.get(`${apiUrl}/api/estimated_cost/phaseMaster`, {
    params: { companyID },
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response;
};

export const fetchPOCheck = async (
  companyID: string,
  po: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/purchase_order/checkPO`, {
    signal,
    params: { companyID, po },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  const result = response.data;
  return result;
};

export const fetchCOCheck = async (
  companyID: string,
  job: string,
  changeOrder: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/change_order/checkCO`, {
    signal,
    params: { companyID, job, changeOrder },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  const result = response.data;
  return result;
};

export const fetchUnitsOfMeasure = async (signal?: AbortSignal) => {
  const response = await axios.get(
    `${apiUrl}/api/purchase_order/unitsOfMeasure`,
    {
      signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  const result = response.data;
  return result;
};

export const fetchJobCostCodes = async (
  companyID: string,
  job: string,
  costType?: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/purchase_order/costCodes`, {
    signal,
    params: { companyID, job, costType },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  const result = response.data;
  return result;
};

export const fetchPhaseMasterCostCodes = async (
  companyID: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(
    `${apiUrl}/api/jobsContracts/phaseMaster/costCodes`,
    {
      signal,
      params: { companyID },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  const result = response.data;
  return result;
};

export const fetchPhaseMasterCostTypes = async (
  companyID: string,
  phase: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(
    `${apiUrl}/api/jobsContracts/phaseMaster/costType`,
    {
      signal,
      params: { companyID, phase },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  const result = response.data;
  return result;
};

export const fetchJobExtras = async (
  companyID: string,
  job: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/purchase_order/extras`, {
    signal,
    params: { companyID, job },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  const result = response.data;
  return result;
};

export const postNewPO = async (body: POArgs) => {
  const { poFile, companyID, ...payload } = body;
  const cleanBody = cleanJsonEmptyValues(payload);

  const response = await axios.post(
    `${apiUrl}/api/purchase_order/newPO`,
    { ...cleanBody, companyID, poFile },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export type PostChangeOrdersArgs = {
  customer: number;
  job: string;
  changeOrder: string;
  description: string;
  totalAmount: number;
  lines: {
    item: number;
    description: string;
    amount: number;
    phases?: {
      code: string;
      description?: string;
      details?: { costType: number; estimate: number }[];
    }[];
  }[];
};

export const postChangeOrders = async (
  company: Company,
  body: PostChangeOrdersArgs,
  file: File
) => {
  const cleanBody = cleanJsonEmptyValues(body);

  const response = await axios.post(
    `${apiUrl}/api/change_order/newCO`,
    { ...cleanBody, companyID: company.id, file },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export type JobPhaseCodesArgs = {
  job: string;
  costCodes: JobPhasesCostCode[];
};

export const postPhaseCodes = async (
  company: Company,
  body: JobPhaseCodesArgs
) => {
  const cleanBody = cleanJsonEmptyValues(body);

  const response = await axios.post(
    `${apiUrl}/api/estimated_cost/newPhases`,
    { ...cleanBody, companyID: company.id },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const fetchSLCheck = async (
  companyID: string,
  sl: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/subcontract/checkSL`, {
    signal,
    params: { companyID, sl },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  const result = response.data;
  return result;
};

export const postNewSL = async (companyID: string, body: SLArgs) => {
  const { subFile, attachment, ...payload } = body;
  const cleanBody = cleanJsonEmptyValues(payload);

  const response = await axios.post(
    `${apiUrl}/api/subcontract/new`,
    { ...cleanBody, companyID, subFile, attachment },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const fetchSLChangeOrderItems = async (
  params: { companyID: string; sl?: string },
  signal?: AbortSignal
) => {
  const cleanedParams = cleanJsonEmptyValues(params);
  const response = await axios.get(
    `${apiUrl}/api/subcontract/changeOrder/items`,
    {
      signal,
      params: cleanedParams,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const postSLChangeOrders = async (
  company: Company,
  body: SubcontractChangeOrderArgs
) => {
  const cleanedBody = cleanJsonEmptyValues(body);
  const response = await axios.post(
    `${apiUrl}/api/subcontract/changeOrder/new`,
    { companyID: company.id, ...cleanedBody },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const fetchVendorTypes = async (signal?: AbortSignal) => {
  const response = await axios.get(`${apiUrl}/api/apEntry/vendor/types`, {
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const data = response.data;
  return data;
};

export const fetchAPEntryDrafts = async (
  companyID: string,
  signal?: AbortSignal
): Promise<APEntryDraftSummary[]> => {
  const response = await axios.get(`${apiUrl}/api/apEntry`, {
    signal,
    params: { companyID },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const postAPTransactionEntry = async (
  body: APTransactionEntryArgs
): Promise<APTransactionEntryDraft> => {
  const { attachment, ...otherFields } = body;

  const response = await axios.post(
    `${apiUrl}/api/apEntry`,
    { attachment, ...cleanJsonEmptyValues(otherFields) },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const putAPTransactionEntry = async (
  id: string,
  body: APTransactionEntryUpdateArgs
): Promise<APTransactionEntryDraft> => {
  const { attachment, ...otherFields } = body;

  const response = await axios.put(
    `${apiUrl}/api/apEntry/${id}`,
    { attachment, ...cleanJsonEmptyValues(otherFields) },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const putSubmitAPTransactionEntry = async (id: string) => {
  const response = await axios.put(
    `${apiUrl}/api/apEntry/submit/${id}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const fetchSubmittedAPEntries = async (
  companyID: string,
  signal?: AbortSignal
): Promise<APEntryDraftSummary[]> => {
  const response = await axios.get(`${apiUrl}/api/apEntry/submit`, {
    signal,
    params: { companyID },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const fetchAPEntryById = async (
  id: string,
  signal?: AbortSignal
): Promise<APTransactionEntryDraft> => {
  const response = await axios.get(`${apiUrl}/api/apEntry/${id}`, {
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const fetchAPEntryAttachment = async (
  id: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/apEntry/${id}/file`, {
    signal,
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response;
};

export const putApproveAPTransactionEntry = async (id: string) => {
  const response = await axios.put(
    `${apiUrl}/api/apEntry/approve/${id}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  return response.data;
};

export const putUploadAPTransactionEntry = async (id: string) => {
  const response = await axios.put(
    `${apiUrl}/api/apEntry/upload/${id}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  return response.data;
};

export const deleteAPTransactionEntry = async (id: string) => {
  const response = await axios.delete(`${apiUrl}/api/apEntry/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  return response.data;
};

export const putRejectAPTransactionEntry = async (
  id: string,
  reason: string
) => {
  const response = await axios.put(
    `${apiUrl}/api/apEntry/reject/${id}`,
    { reason },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  return response.data;
};

export type DocumentDropOffArgs = {
  companyID: string;
  section: string;
  documents: File[];
  notes: string;
  attentionTo?: string;
};

export const postDocumentDropOff = async (
  body: DocumentDropOffArgs
): Promise<APTransactionEntryDraft> => {
  const { documents, ...otherFields } = body;
  const formData = new FormData();
  documents.forEach((file) => {
    formData.append('documents', file);
  });

  const cleanBody = cleanJsonEmptyValues(otherFields);
  for (const key of Object.keys(cleanBody)) {
    formData.append(key, String(cleanBody[key]));
  }

  const response = await axios.post(
    `${apiUrl}/api/accounting_forms/dropOff`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};
